import React, { useState } from "react";
import {
  Box,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Typography,
  Chip,
  Avatar,
  Stack,
} from "@mui/material";
import PreviewIcon from "@mui/icons-material/Preview";
import DeleteIcon from "@mui/icons-material/Delete";
import CollectionsBookmarkRoundedIcon from "@mui/icons-material/CollectionsBookmarkRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import PublishedWithChangesRoundedIcon from "@mui/icons-material/PublishedWithChangesRounded";
import UnpublishedRoundedIcon from "@mui/icons-material/UnpublishedRounded";
import ICollection from "../../entities/ICollection";
import CollectionService from "../../services/CollectionService";
import { getErrorMessage, resolveImageUrl } from "../../services/apiUtils";
import { theme } from "../../theme";

export const COLLECTION_STATUS = {
  INACTIVE: 0,
  ACTIVE: 1,
} as const;

export const isCollectionActive = (collection: Pick<ICollection, "statusId">): boolean =>
  collection.statusId === true || Number(collection.statusId) === COLLECTION_STATUS.ACTIVE;

type CollectionActionsProps = {
  collection: ICollection;
  onDeleted?: () => void;
  onStatusChanged?: () => void;
};

const CollectionActions: React.FC<CollectionActionsProps> = ({ collection, onDeleted, onStatusChanged }) => {
  const [viewOpen, setViewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const collectionId = collection.id ?? collection.collectionId;
  const isActive = isCollectionActive(collection);

  const products =
    collection.collectionProducts ||
    collection.collectionProductRequestDtos ||
    collection.collectionProductList ||
    [];

  const handleDelete = () => {
    setDeleting(true);
    CollectionService.deleteCollection(collectionId as number)
      .then(() => {
        setSnackbar({ open: true, message: "Collection deleted.", severity: "success" });
        setConfirmOpen(false);
        onDeleted?.();
      })
      .catch((err: any) => {
        setSnackbar({
          open: true,
          message: getErrorMessage(err, "Could not delete this collection."),
          severity: "error",
        });
      })
      .finally(() => setDeleting(false));
  };

  const handleToggleStatus = () => {
    const nextStatus = isActive ? COLLECTION_STATUS.INACTIVE : COLLECTION_STATUS.ACTIVE;
    setStatusUpdating(true);
    CollectionService.updateCollection(collectionId as number, { ...collection, statusId: nextStatus })
      .then(() => {
        setSnackbar({
          open: true,
          message: isActive
            ? "Collection deactivated. It will no longer show on the website."
            : "Collection activated. It will now show on the website.",
          severity: "success",
        });
        onStatusChanged?.();
      })
      .catch((err: any) => {
        setSnackbar({
          open: true,
          message: getErrorMessage(err, "Could not update the collection status."),
          severity: "error",
        });
      })
      .finally(() => setStatusUpdating(false));
  };

  return (
    <Box>
      <Tooltip title="View Collection details">
        <IconButton onClick={() => setViewOpen(true)} sx={{ color: theme.palette.secondary.main }}>
          <PreviewIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title={isActive ? "Deactivate Collection" : "Activate Collection"}>
        <span>
          <IconButton
            disabled={statusUpdating}
            onClick={handleToggleStatus}
            sx={{ color: isActive ? "#f1b44c" : theme.status.success }}
          >
            {isActive ? <UnpublishedRoundedIcon /> : <PublishedWithChangesRoundedIcon />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Delete Collection">
        <IconButton onClick={() => setConfirmOpen(true)} sx={{ color: theme.status.danger }}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>

      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #FDB44D 100%)`,
            color: "#fff",
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 48, height: 48 }}>
            <CollectionsBookmarkRoundedIcon />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight="bold" noWrap>
              {collection.name || "Untitled Collection"}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              Collection ID: {collectionId}
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="overline" color="text.secondary">
            Products ({products.length})
          </Typography>
          {products.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              No products in this collection.
            </Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
              {products.map((p, idx) => (
                <Chip
                  key={p.productId ?? idx}
                  avatar={
                    <Avatar src={resolveImageUrl(p.thumbnail) || undefined}>
                      <Inventory2RoundedIcon fontSize="small" />
                    </Avatar>
                  }
                  label={p.productTitle || `Product #${p.productId}`}
                />
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setViewOpen(false)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle>Delete Collection</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{collection.name}"? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CollectionActions;
